export type GenericPagedResult<R> = PagedResults<R> | NextPagedResults<R> | MaxPagedResults<R>;

function isPagedResults<R>(pagedResult: GenericPagedResult<R>): pagedResult is PagedResults<R> {
    return (pagedResult as PagedResults<R>).totalcount != undefined;
}

export function isMaxPagedResults<R>(pagedResult: GenericPagedResult<R>): pagedResult is MaxPagedResults<R> {
    return (pagedResult as MaxPagedResults<R>).haveMore != undefined;
}

export function isNextPagedResults<R>(pagedResult: GenericPagedResult<R>): pagedResult is NextPagedResults<R> {
    return (pagedResult as NextPagedResults<R>).nextKey != undefined;
}

export interface PagedResults<R = unknown> {
    totalcount: number;
    offset: number;
    records: R[];
    count: number;
}

export interface MaxPagedResults<R = unknown> {
    haveMore: boolean;
    records: R[];
    count: number;
}

export interface NextPagedResults<R = unknown> {
    nextKey?: string | null;
    records: R[];
    count: number;
}

export class EmptyNextPagedResults<R> implements NextPagedResults {
    nextKey = undefined;
    records: R[] = [];
    count = 0;
}

export class EmptyMaxPagedResults<R> implements MaxPagedResults {
    haveMore = false;
    records: R[] = [];
    count = 0;
}

export class EmptyPagedResults<R> implements PagedResults {
    totalcount = 99999999;
    offset = 0;
    records: R[] = [];
    count = 0;
}

export type GeneralPagingParams = PagingParams | string | null;

export class PagingParams {
    offset = 0;
    count = 25;
}

interface DbGisPage {
    page: number;
    size: number;
    totalElements: number;
}

export interface DbGisServiceResponse<R = unknown> {
    service: string;
    pages: DbGisPage;
    entities: R[];
}

export interface DbGisRestResponse {
    services: DbGisServiceResponse[];
}

export function dbGIStoStandardPage<R = unknown>(dbgis: DbGisRestResponse | undefined, pageSize: number, mapper?: (entity: any) => R): PagedResults<R> {
    if (!dbgis || !dbgis.services) {
        return new EmptyPagedResults<R>();
    }
    const service = dbgis.services[0] as DbGisServiceResponse<R>;

    let records = service.entities;
    if (mapper) {
        records = records.map(mapper);
    }

    const ret: PagedResults<R> = {
        totalcount: service.pages.totalElements,
        offset: (service.pages.page - 1) * pageSize,
        count: service.pages.size,
        records
    };

    return ret;
}

export function standardPagingParamToDbGIS(params: PagingParams, pageSize: number): PagingParams {
    return {
        offset: Math.floor(params.offset / pageSize) + 1,
        count: params.count
    }
}

export function mergePages<R>(one: PagedResults<R>, two: PagedResults<R>): PagedResults<R> {
    const ret: PagedResults<R> = {
        totalcount: two.totalcount,
        offset: one.offset,
        count: one.count + two.count,
        records: [...one.records, ...two.records]
    }

    return ret;
}

export async function infiniteLoad<R>(loader: (paging: PagingParams) => Promise<PagedResults<R>>, page?: PagedResults<R>): Promise<PagedResults<R> | null> {
    if (!page) {
        page = new EmptyPagedResults();
    }
    const paging = new PagingParams();
    paging.offset = page.offset + page.count;

    if (paging.offset >= page.totalcount) {
        return null;
    }

    const newPage = await loader(paging);

    if (newPage.totalcount === 0) {
        return null;
    }

    return mergePages(page, newPage);
}

export function mergeNextPages<R>(one: NextPagedResults<R>, two: NextPagedResults<R>): NextPagedResults<R> {
    return {
        count: one.count + two.count,
        nextKey: two.nextKey,
        records: [...one.records, ...two.records]
    }
}

export async function infiniteNextLoad<R>(loader: (nextKey?: string) => Promise<NextPagedResults<R>>, previousPage?: NextPagedResults<R>): Promise<NextPagedResults<R> | null> {
    if (!previousPage) {
        previousPage = new EmptyNextPagedResults();
    }
    if (previousPage.nextKey === null || previousPage.nextKey === "") {
        return null;
    }
    const newPage = await loader(previousPage.nextKey);
    if(newPage.nextKey == null && newPage.count === 0 && previousPage.nextKey === undefined){
        return null;
    }

    return mergeNextPages(previousPage, newPage)
}
